import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import SEO from '../components/SEO';
import { blogPosts } from '../data/blogPosts';

export default function BlogList() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const schema = {
    '@context': 'https://schema.org',
    '@type': 'Blog',
    'name': 'Vigor Auto Repair Blog',
    'description': 'Automotive service, maintenance and repair insights for Dubai drivers.',
    'url': typeof window !== 'undefined' ? window.location.href : 'https://www.example.com/blog'
  };

  // Always show newest posts first
  const posts = [...blogPosts].sort((a, b) => new Date(b.published) - new Date(a.published));
  const aggregatedKeywords = posts
    .map(p => (p.seo?.keywords || '')).filter(Boolean)
    .join(', ');

  return (
    <>
  <SEO title="Car Care Blog - Vigor Auto Ras Al Khor Dubai | Maintenance Tips & Guides" description="Expert car maintenance tips, repair guides & automotive advice from Vigor Auto Ras Al Khor Dubai. Learn from certified technicians about car service, diagnostics, and UAE-specific vehicle care." keywords={`car maintenance dubai, auto repair tips, car care guides Ras Al Khor, car service blog dubai, ${aggregatedKeywords}`} schema={schema} />
  <header className="pt-20 md:pt-28 pb-10 md:pb-14 bg-gradient-to-br from-blue-900 via-blue-800 to-indigo-900 text-white relative overflow-hidden">
        <div className="pointer-events-none absolute inset-0 opacity-10 bg-[radial-gradient(circle_at_70%_30%,#F5D313,transparent_60%)]" />
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <p className="uppercase tracking-widest text-xs font-semibold text-[#F5D313]/80">{t('blog.badge')}</p>
          <h1 className="mt-4 text-4xl md:text-5xl font-extrabold tracking-tight leading-tight">{t('blog.title')}</h1>
          <p className="mt-6 text-slate-300 text-base md:text-lg leading-relaxed max-w-3xl">{t('blog.subtitle')}</p>
        </div>
      </header>
  <main className="py-10 md:py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid gap-10 md:grid-cols-2">
            {posts.map(post => (
              <article
                key={post.slug}
                className="group border border-slate-200 rounded-2xl p-6 bg-white hover:shadow-md transition cursor-pointer"
                role="link"
                tabIndex={0}
                onClick={() => navigate(`/blog/${post.slug}`)}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); navigate(`/blog/${post.slug}`); } }}
              >
                <header>
                  {post.image && (
                    <div className="-mt-2 -mx-2 mb-4 overflow-hidden rounded-xl">
                      <img src={post.image} alt={post.title} className="w-full h-40 object-cover transition-transform group-hover:scale-[1.02]" loading="lazy" />
                    </div>
                  )}
                  <h2 className="text-xl font-semibold tracking-tight mb-2 text-slate-800 group-hover:text-slate-900">{post.title}</h2>
                  <p className="text-xs text-slate-500 mb-3 flex gap-3"><span>{new Date(post.published).toLocaleDateString()}</span><span>{post.readingMinutes} {t('blog.readTime')}</span></p>
                </header>
                <p className="text-sm text-slate-600 leading-relaxed mb-4 line-clamp-4">{post.excerpt}</p>
                <Link to={`/blog/${post.slug}`} className="inline-flex items-center gap-1 text-xs font-semibold uppercase tracking-wide text-slate-700 group-hover:text-slate-900">
                  {t('common.readMore')}
                  <svg className="w-3 h-3" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24"><path d="m9 18 6-6-6-6" /></svg>
                </Link>
              </article>
            ))}
          </div>
        </div>
      </main>
    </>
  );
}
